// Echarts
export const colorPalette = [
  "#1b4e6b",
  "#5C63A2",
  "#C068A8",
  "#EC7176",
  "#f4ab33",
];

export const grayScallePallete = [
  "#CCCCCC",
  "#D1D1D1",
  "#D6D6D6",
  "#DBDBDB",
  "#E0E0E0",
  "#E6E6E6",
];

export enum BaseColors {
  Background = "#F5F5F5",
}

// Sentiment Thresholds
export enum SentimentColors {
  Positive = "#00876C",
  Neutral = "#CFC1A8",
  Negative = "#DE425B",
}

// Cumulative Effort Thresholds
export enum EffortColors {
  Low = "#FFA39E",
  Moderate = "#FF5950",
  High = "#BE170E",
}

// Echarts Special QueryField Lines
export enum SpecialQFColors {
  Sentiment = "#1798F9",
  Effort = "#FF5950",
}

export enum PaletteRoles {
  backgroundColor,
  primaryColor,
  secondaryColor,
  sentimentColor,
  effortColor,
  negativeTextColor,
  positiveSentiment,
  neutralSentiment,
  negativeSentiment,
  highEffort,
  moderateEffort,
  lowEffort,
  shadowColor,
  axisLabelColor,
  axisLabelBorderShadowColor,
  axisLabelColorContrast,
  axisLabelBorderShadowColorContrast,
}
export class ColorOption {
  hexValue: string;
  tagName?: string;
  roles?: Array<PaletteRoles> = [];
  hide?: boolean;
}

export const specialColors: Array<ColorOption> = [
  {
    hexValue: "#1798F9",
    roles: [PaletteRoles.sentimentColor],
    hide: true,
  },
  {
    hexValue: "#FF5950",
    roles: [PaletteRoles.effortColor],
    hide: true,
  },
];

export const defaultCommons: Array<ColorOption> = [
  {
    hexValue: "#F5F5F5",
    roles: [PaletteRoles.backgroundColor],
    hide: true,
  },
  {
    hexValue: "#333",
    roles: [PaletteRoles.shadowColor],
    hide: true,
  },
  {
    hexValue: "#333",
    roles: [PaletteRoles.axisLabelColor],
    hide: true,
  },
  {
    hexValue: "#ffffff",
    roles: [PaletteRoles.axisLabelBorderShadowColor],
    hide: true,
  },
  {
    hexValue: "#ffffff",
    roles: [PaletteRoles.axisLabelColorContrast],
    hide: true,
  },
  {
    hexValue: "#000000",
    roles: [PaletteRoles.axisLabelBorderShadowColorContrast],
    hide: true,
  },
  ...specialColors,
];

const default_theme: Array<ColorOption> = [
  {
    hexValue: "#1b4e6b",
    roles: [PaletteRoles.primaryColor],
  },
  { hexValue: "#5C63A2" },
  { hexValue: "#C068A8" },
  { hexValue: "#EC7176" },
  { hexValue: "#f4ab33" },
  {
    hexValue: "#000000",
    roles: [PaletteRoles.negativeTextColor],
    hide: true,
  },
  ...defaultCommons,
];

export class Palette {
  public constructor(init?: Partial<Palette>) {
    Object.assign(this, init);
  }

  palette_label = "Default Theme";
  palette_name = "default_theme";
  showColors = false;
  background = false;
  colors: Array<ColorOption> = default_theme;

  public get_color_array?(): Array<string> {
    const filtered = this.colors.filter((t) => {
      return !t.hide;
    });
    return filtered.map((x) => x.hexValue);
  }

  public get_color_sequence?(): Array<string> {
    const color_count = this.get_color_array().length;
    return Array.from(
      { length: 11 },
      (v, k) => this.get_color_array()[k % color_count],
    );
  }

  public get primary_color(): string {
    return this.get_color_by_role(PaletteRoles.primaryColor);
  }

  public get secondary_color(): string {
    try {
      return this.get_color_by_role(PaletteRoles.secondaryColor);
    } catch (error) {
      return this.get_color_array()[this.get_color_array().length - 1];
    }
  }

  public get background_color(): string {
    return this.get_color_by_role(PaletteRoles.backgroundColor);
  }

  public get effort_color(): string {
    return this.get_color_by_role(PaletteRoles.effortColor);
  }

  public get sentiment_color(): string {
    return this.get_color_by_role(PaletteRoles.sentimentColor);
  }

  public get text_color(): string {
    return this.get_color_by_role(PaletteRoles.negativeTextColor);
  }

  public get shadow_color(): string {
    return this.get_color_by_role(PaletteRoles.shadowColor);
  }

  public get axis_label_color(): string {
    return this.get_color_by_role(PaletteRoles.axisLabelColor);
  }

  public get axis_label_border_shadow_color(): string {
    return this.get_color_by_role(PaletteRoles.axisLabelBorderShadowColor);
  }

  public get axis_label_color_contrast(): string {
    return this.get_color_by_role(PaletteRoles.axisLabelColorContrast);
  }

  public get axis_label_border_shadow_color_contrast(): string {
    return this.get_color_by_role(
      PaletteRoles.axisLabelBorderShadowColorContrast,
    );
  }

  public get_color_by_role?(role: PaletteRoles): string {
    try {
      return this.colors.find((x) => x.roles && x.roles.some((f) => f === role))
        .hexValue;
    } catch (error) {
      return default_theme.find(
        (x) => x.roles && x.roles.some((f) => f === role),
      ).hexValue;
    }
  }

  public get_threshold_color?(item): string {
    try {
      if (this === sentimentThreshold) {
        return this.get_sentiment_threshold_color(item.sentiment);
      } else if (this === effortThreshold) {
        return this.get_effort_threshold_color(item.cumulativeEffort);
      }
    } catch (error) {
      return null;
    }
  }

  public get_sentiment_threshold_color?(sentiment_value): string {
    try {
      if (sentiment_value > 1) {
        return this.colors.find(
          (x) =>
            x.roles &&
            x.roles.some((f) => f === PaletteRoles.positiveSentiment),
        ).hexValue;
      } else if (sentiment_value >= -1 || 1 <= sentiment_value) {
        return this.colors.find(
          (x) =>
            x.roles && x.roles.some((f) => f === PaletteRoles.neutralSentiment),
        ).hexValue;
      } else if (sentiment_value < 1) {
        return this.colors.find(
          (x) =>
            x.roles &&
            x.roles.some((f) => f === PaletteRoles.negativeSentiment),
        ).hexValue;
      }
    } catch (error) {
      return null;
    }
  }

  public get_effort_threshold_color?(effort_value: number): string {
    try {
      if (effort_value > 3.33) {
        return this.colors.find(
          (x) => x.roles && x.roles.some((f) => f === PaletteRoles.highEffort),
        ).hexValue;
      } else if (effort_value >= 1.67 || 3.33 < effort_value) {
        return this.colors.find(
          (x) =>
            x.roles && x.roles.some((f) => f === PaletteRoles.moderateEffort),
        ).hexValue;
      } else if (effort_value < 1.67) {
        return this.colors.find(
          (x) => x.roles && x.roles.some((f) => f === PaletteRoles.lowEffort),
        ).hexValue;
      }
    } catch (error) {
      return null;
    }
  }
}

export const defaultPalette: Palette = new Palette();

export class ColorPalleteOptions {
  constructor(value: ColorPalleteOptions) {
    this.groups = value.groups;
  }
  groups: Array<OptionGroup>;
}

export interface OptionGroup {
  group_name: string;
  palettes: Array<Palette>;
}

const magenta: Array<ColorOption> = [
  {
    hexValue: "#892559",
    roles: [PaletteRoles.primaryColor],
  },
  { hexValue: "#c2387f" },
  { hexValue: "#ce64a0" },
  { hexValue: "#de98bf" },
  ...defaultCommons,
];

const dark: Array<ColorOption> = [
  { hexValue: "#b5b5cc" },
  { hexValue: "#7e9ec7" },
  {
    hexValue: "#b4d3ed",
    roles: [PaletteRoles.primaryColor],
  },
  { hexValue: "#afd8d9" },
  {
    hexValue: "#dfeec6",
    roles: [PaletteRoles.secondaryColor],
  },
  {
    hexValue: "#333333",
    roles: [PaletteRoles.backgroundColor],
    hide: true,
  },
  {
    hexValue: BaseColors.Background,
    roles: [PaletteRoles.negativeTextColor],
    hide: true,
  },
  {
    hexValue: "#b5b5cc",
    roles: [PaletteRoles.shadowColor],
    hide: true,
  },
  {
    hexValue: "#ffffff",
    roles: [PaletteRoles.axisLabelColor],
    hide: true,
  },
  {
    hexValue: "#000000",
    roles: [PaletteRoles.axisLabelBorderShadowColor],
    hide: true,
  },
  {
    hexValue: "#ffffff",
    roles: [PaletteRoles.axisLabelColorContrast],
    hide: true,
  },
  {
    hexValue: "#000000",
    roles: [PaletteRoles.axisLabelBorderShadowColorContrast],
    hide: true,
  },
  ...specialColors,
];

const orange: Array<ColorOption> = [
  {
    hexValue: "#f56604",
    roles: [PaletteRoles.primaryColor],
  },
  { hexValue: "#f48800" },
  { hexValue: "#f3ae46" },
  { hexValue: "#f8d197" },
  ...defaultCommons,
];

const yellow: Array<ColorOption> = [
  {
    hexValue: "#FFC000",
    roles: [PaletteRoles.primaryColor],
  },
  { hexValue: "#FFDC4D" },
  { hexValue: "#FFE780" },
  { hexValue: "#FFF6CD" },
  ...defaultCommons,
];

const teal: Array<ColorOption> = [
  {
    hexValue: "#006b6e",
    roles: [PaletteRoles.primaryColor],
  },
  { hexValue: "#299d9f" },
  { hexValue: "#7cdfc1" },
  { hexValue: "#b3edd9" },
  ...defaultCommons,
];

const blue: Array<ColorOption> = [
  {
    hexValue: "#1c4782",
    roles: [PaletteRoles.primaryColor],
  },
  { hexValue: "#216cc2" },
  { hexValue: "#6da8db" },
  { hexValue: "#b4d3ed" },
  ...defaultCommons,
];

const aqua: Array<ColorOption> = [
  {
    hexValue: "#104357",
    roles: [PaletteRoles.primaryColor],
  },
  { hexValue: "#196a8b" },
  { hexValue: "#50869e" },
  { hexValue: "#8fbec6" },
  { hexValue: "#afd8d9" },
  ...defaultCommons,
];

const blueViolet: Array<ColorOption> = [
  {
    hexValue: "#000565",
    roles: [PaletteRoles.primaryColor],
  },
  { hexValue: "#2E3399" },
  { hexValue: "#666BCC" },
  { hexValue: "#B8BAE6" },
  ...defaultCommons,
];

const purple: Array<ColorOption> = [
  {
    hexValue: "#330066",
    roles: [PaletteRoles.primaryColor],
  },
  { hexValue: "#632E99" },
  { hexValue: "#9B66CC" },
  { hexValue: "#CFB8E6" },
  ...defaultCommons,
];

const sentiment: Array<ColorOption> = [
  {
    hexValue: SentimentColors.Positive,
    roles: [PaletteRoles.positiveSentiment],
    hide: true,
  },
  {
    hexValue: SentimentColors.Neutral,
    roles: [PaletteRoles.neutralSentiment],
    hide: true,
  },
  {
    hexValue: SentimentColors.Negative,
    roles: [PaletteRoles.negativeSentiment],
    hide: true,
  },
  ...default_theme,
];

export const sentimentThreshold = new Palette({
  palette_label: "Sentiment",
  colors: sentiment,
  palette_name: "sentiment",
  showColors: false,
  background: false,
});

const effort: Array<ColorOption> = [
  {
    hexValue: EffortColors.High,
    roles: [PaletteRoles.highEffort],
    hide: true,
  },
  {
    hexValue: EffortColors.Moderate,
    roles: [PaletteRoles.moderateEffort],
    hide: true,
  },
  {
    hexValue: EffortColors.Low,
    roles: [PaletteRoles.lowEffort],
    hide: true,
  },
  ...default_theme,
];

export const effortThreshold = new Palette({
  palette_label: "Cumulative Effort",
  colors: effort,
  palette_name: "cumulative_effort",
  showColors: false,
  background: false,
});

export const ColorThemes = new ColorPalleteOptions({
  groups: [
    {
      group_name: "Themes",
      palettes: [
        new Palette({ showColors: true }),
        new Palette({
          palette_label: "Dark Theme",
          colors: dark,
          palette_name: "dark_theme",
          showColors: true,
          background: true,
        }),
      ],
    },
    {
      group_name: "Monochromatic",
      palettes: [
        new Palette({
          palette_label: "Orange",
          colors: orange,
          palette_name: "orange",
          showColors: true,
        }),
        new Palette({
          palette_label: "Yellow",
          colors: yellow,
          palette_name: "yellow",
          showColors: true,
        }),
        new Palette({
          palette_label: "Magenta",
          colors: magenta,
          palette_name: "magenta",
          showColors: true,
        }),
        new Palette({
          palette_label: "Teal",
          colors: teal,
          palette_name: "teal",
          showColors: true,
        }),
        new Palette({
          palette_label: "Aqua",
          colors: aqua,
          palette_name: "aqua",
          showColors: true,
        }),
        new Palette({
          palette_label: "Blue",
          colors: blue,
          palette_name: "blue",
          showColors: true,
        }),
        new Palette({
          palette_label: "Blue Violet",
          colors: blueViolet,
          palette_name: "blueViolet",
          showColors: true,
        }),
        new Palette({
          palette_label: "Purple",
          colors: purple,
          palette_name: "purple",
          showColors: true,
        }),
      ],
    },
  ],
});

export const ColorThemesSurvey = new ColorPalleteOptions({
  groups: ColorThemes.groups.concat([
    {
      group_name: "Thresholds",
      palettes: [sentimentThreshold],
    },
  ]),
});

export const ColorThemesChat = new ColorPalleteOptions({
  groups: ColorThemes.groups.concat([
    {
      group_name: "Thresholds",
      palettes: [sentimentThreshold, effortThreshold],
    },
  ]),
});

// Predictor Colors
export enum PredictorColors {
  Intents = "#0BA4B2",
  Emotions = "#FFB10D",
  Fluff = "#6482D3",
  Topics = "#FF7F0D",
  Categories = "#0BA4B2",
}
